// Simple markdown renderer for legal pages — handles the subset used in our docs.
// No external dependencies needed.

function renderInline(text) {
  // **bold** and *italic* — split on markers and alternate
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldIdx  = remaining.indexOf('**');
    const italicIdx = remaining.indexOf('*');

    if (boldIdx !== -1 && (italicIdx === -1 || boldIdx <= italicIdx)) {
      const end = remaining.indexOf('**', boldIdx + 2);
      if (end !== -1) {
        if (boldIdx > 0) parts.push(remaining.slice(0, boldIdx));
        parts.push(<strong key={key++}>{remaining.slice(boldIdx + 2, end)}</strong>);
        remaining = remaining.slice(end + 2);
        continue;
      }
    }

    if (italicIdx !== -1) {
      const start = italicIdx;
      const end   = remaining.indexOf('*', start + 1);
      if (end !== -1) {
        if (start > 0) parts.push(remaining.slice(0, start));
        parts.push(<em key={key++}>{remaining.slice(start + 1, end)}</em>);
        remaining = remaining.slice(end + 1);
        continue;
      }
    }

    parts.push(remaining);
    break;
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

function parseMarkdown(markdown) {
  const blocks = markdown.split(/\n{2,}/);
  const elements = [];

  blocks.forEach((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return;

    // Horizontal rule
    if (trimmed === '---') {
      elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid #1e2538', margin: '24px 0' }} />);
      return;
    }

    // Headings
    if (trimmed.startsWith('### ')) {
      elements.push(<h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: '#e8e0d8', margin: '24px 0 8px', letterSpacing: 0.2 }}>{renderInline(trimmed.slice(4))}</h3>);
      return;
    }
    if (trimmed.startsWith('## ')) {
      elements.push(<h2 key={i} style={{ fontSize: 18, fontWeight: 700, color: '#e8e0d8', margin: '32px 0 10px', paddingBottom: 6, borderBottom: '1px solid #1e2538', letterSpacing: 0.2 }}>{renderInline(trimmed.slice(3))}</h2>);
      return;
    }
    if (trimmed.startsWith('# ')) {
      elements.push(<h1 key={i} style={{ fontSize: 24, fontWeight: 700, color: '#e8e0d8', margin: '0 0 6px', fontFamily: 'Georgia, serif', letterSpacing: 0.3 }}>{renderInline(trimmed.slice(2))}</h1>);
      return;
    }

    // Italic-only block (e.g. *disclaimer footer*)
    if (trimmed.startsWith('*') && trimmed.endsWith('*') && !trimmed.startsWith('**')) {
      elements.push(<p key={i} style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7, margin: '16px 0', fontStyle: 'italic' }}>{trimmed.slice(1, -1)}</p>);
      return;
    }

    // Unordered list
    const lines = trimmed.split('\n');
    if (lines.every(l => l.trim().startsWith('- '))) {
      elements.push(
        <ul key={i} style={{ paddingLeft: 20, margin: '8px 0 16px', color: '#c9c2ba', fontSize: 14, lineHeight: 1.8 }}>
          {lines.map((l, j) => <li key={j}>{renderInline(l.trim().slice(2))}</li>)}
        </ul>
      );
      return;
    }

    // Mixed block — some lines may be list items after a label line
    if (lines.some(l => l.trim().startsWith('- '))) {
      elements.push(
        <div key={i} style={{ margin: '8px 0 16px' }}>
          {lines.map((l, j) => {
            const t = l.trim();
            if (t.startsWith('- ')) {
              return <li key={j} style={{ marginLeft: 20, fontSize: 14, color: '#c9c2ba', lineHeight: 1.8, listStyleType: 'disc' }}>{renderInline(t.slice(2))}</li>;
            }
            return <p key={j} style={{ fontSize: 14, color: '#c9c2ba', lineHeight: 1.7, margin: '0 0 4px' }}>{renderInline(t)}</p>;
          })}
        </div>
      );
      return;
    }

    // Regular paragraph (join multi-line blocks)
    const text = lines.join(' ');
    elements.push(<p key={i} style={{ fontSize: 14, color: '#c9c2ba', lineHeight: 1.8, margin: '0 0 16px' }}>{renderInline(text)}</p>);
  });

  return elements;
}

export default function LegalPage({ title, content }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0f1a',
      fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
      padding: '32px 16px 64px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <div style={{ width: '100%', maxWidth: 720 }}>
        {/* Back button */}
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : window.location.replace('/')}
          style={{
            background: 'none', border: 'none', color: '#9ca3af',
            fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            padding: '0 0 24px', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          ← Back
        </button>

        {/* Content */}
        <div style={{ color: '#e8e0d8' }}>
          {parseMarkdown(content)}
        </div>

        {/* Footer */}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid #1e2538', fontSize: 12, color: '#4b5563', textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <a href="/councils/" style={{ color: '#9ca3af', textDecoration: 'none' }}>Browse public DA pages</a>
            <a href="/privacy" style={{ color: '#9ca3af', textDecoration: 'none' }}>Privacy</a>
            <a href="/terms" style={{ color: '#9ca3af', textDecoration: 'none' }}>Terms</a>
          </div>
          © 2026 SiteScope PropTech
        </div>
      </div>
    </div>
  );
}
